import { useState, useCallback, useRef } from 'react';
import { Editor, Transforms } from 'slate';
import { TagElement } from 'slate/types/slate';
import { useDeleteTag, useBulkDeleteTags, TagWithMetadata } from 'entities/tag';
import { removeTag, isTag, removeBlockTagBinding, findBlockCarrierForTag, relinkPredecessor, withoutUndo } from 'slate/extensions/tag';
import { useSetEditorReferences } from 'app/api/documents/useEditorData';

interface UseDeleteTagWithConfirmationOptions {
  editor: Editor;
  documentSuperId?: number;
  onDeleted?: (tagIds: string[]) => void;
}

type ReferenceCell = {
  tags?: string[];
  [key: string]: unknown;
};

type ReferenceRow = {
  superId?: string;
  selectionContent?: ReferenceCell[][];
  [key: string]: unknown;
};

interface PendingTag {
  id: string;
  tag: TagWithMetadata | null;
}

interface DeleteTagState {
  showModal: boolean;
  pending: PendingTag[];
  removeCompletely: boolean;
  chain: TagWithMetadata[];
}

const EMPTY_STATE: DeleteTagState = { showModal: false, pending: [], removeCompletely: false, chain: [] };

export function useDeleteTagWithConfirmation({ editor, documentSuperId, onDeleted }: UseDeleteTagWithConfirmationOptions) {
  const [state, setState] = useState<DeleteTagState>(EMPTY_STATE);

  const deleteTagMutation = useDeleteTag();
  const bulkDeleteMutation = useBulkDeleteTags();
  const setEditorReferences = useSetEditorReferences();
  const inFlight = useRef(false);
  const onDeletedRef = useRef(onDeleted);
  onDeletedRef.current = onDeleted;

  const requestDelete = useCallback((tag: TagWithMetadata, chain: TagWithMetadata[] = []) => {
    if (inFlight.current) return;
    setState({ showModal: true, pending: [{ id: tag.id, tag }], removeCompletely: false, chain: chain.length > 1 ? chain : [] });
  }, []);

  const requestDeleteById = useCallback((tagId: string) => {
    if (inFlight.current) return;
    setState({ showModal: true, pending: [{ id: tagId, tag: null }], removeCompletely: true, chain: [] });
  }, []);

  const requestDeleteMany = useCallback((tags: TagWithMetadata[]) => {
    if (tags.length === 0 || inFlight.current) return;
    setState({ showModal: true, pending: tags.map((tag) => ({ id: tag.id, tag })), removeCompletely: false, chain: [] });
  }, []);

  const inspectEditor = useCallback(
    (pendingTagId: string, pendingTag: TagWithMetadata | null) => {
      const blockEntry = findBlockCarrierForTag(editor, pendingTagId);
      const isBlockTag = !!blockEntry;
      const isTableTag = !isBlockTag && !!pendingTag?.metadata?.tableName;
      const hasInlineMarker = !Editor.nodes(editor, { at: [], match: (n) => isTag(n) && n.tagId === pendingTagId }).next().done;
      return { blockEntry, isTableTag, hasInlineMarker, removedMarker: isBlockTag || (!isTableTag && hasInlineMarker) };
    },
    [editor]
  );

  const removeFromEditor = useCallback(
    (pendingTagId: string, pendingTag: TagWithMetadata | null, removeCompletely: boolean) => {
      const { blockEntry, isTableTag, hasInlineMarker } = inspectEditor(pendingTagId, pendingTag);

      withoutUndo(editor, () => {
        if (blockEntry) {
          removeBlockTagBinding(editor, blockEntry[1], pendingTagId);
        } else if (!isTableTag) {
          if (removeCompletely) {
            // Remove tag node entirely (tag + text content) — used for last-char deletion
            Editor.withoutNormalizing(editor, () => {
              // Relink continuation chain in Slate before removing
              const entry = Array.from(
                Editor.nodes(editor, {
                  at: [],
                  match: (n) => isTag(n) && n.tagId === pendingTagId,
                })
              )[0];

              if (entry) {
                const [node, path] = entry;
                const predecessor = Array.from(
                  Editor.nodes(editor, {
                    at: [],
                    match: (n) => isTag(n) && n.continuedAt === pendingTagId,
                  })
                )[0];

                const successorId = isTag(node) ? node.continuedAt : undefined;

                if (predecessor && successorId) {
                  Transforms.setNodes(editor, { continuedAt: successorId } as Partial<TagElement>, { at: predecessor[1] });
                } else if (predecessor) {
                  Transforms.setNodes(editor, { continuedAt: undefined } as Partial<TagElement>, { at: predecessor[1] });
                }

                Transforms.removeNodes(editor, { at: path });
              }
            });
          } else if (hasInlineMarker) {
            // Unwrap tag from Slate (keep text) — used for sidebar delete actions
            removeTag(editor, pendingTagId);
          } else {
            relinkPredecessor(editor, pendingTagId, pendingTag?.nextSegment ?? undefined);
          }
        }
      });
    },
    [editor, inspectEditor]
  );

  const runDelete = useCallback(
    (pending: PendingTag[], removeCompletely: boolean) => {
      if (pending.length === 0) return;
      if (pending.length > 1 && documentSuperId == null) return;

      const removals = pending.map(({ id, tag }) => ({ id, tag, ...inspectEditor(id, tag) }));
      const tableTags = removals.filter((r) => r.isTableTag);
      const skipSectionRewrite = removals.every((r) => r.removedMarker);
      const ids = pending.map((p) => p.id);

      const removeFromReferences = () => {
        if (tableTags.length === 0) return;
        const removedByRef = new Map<string, Set<string>>();
        tableTags.forEach(({ id, tag }) => {
          if (!tag?.onId) return;
          const tagIds = removedByRef.get(tag.onId) ?? new Set<string>();
          tagIds.add(id);
          removedByRef.set(tag.onId, tagIds);
        });
        setEditorReferences((current) =>
          current.map((ref: ReferenceRow) => {
            const removed = ref.superId ? removedByRef.get(ref.superId) : undefined;
            if (!removed) return ref;
            const updatedContent = ref.selectionContent?.map((row: ReferenceCell[]) =>
              row.map((cell: ReferenceCell) => {
                if (!cell.tags || !cell.tags.some((t) => removed.has(t))) return cell;
                return { ...cell, tags: cell.tags.filter((t: string) => !removed.has(t)) };
              })
            );
            return { ...ref, selectionContent: updatedContent };
          })
        );
      };

      const applyLocally = () => {
        pending.forEach(({ id, tag }) => removeFromEditor(id, tag, removeCompletely));
        removeFromReferences();
        onDeletedRef.current?.(ids);
      };

      const request =
        pending.length === 1
          ? deleteTagMutation.mutateAsync({ id: ids[0], documentSuperId, skipSectionRewrite })
          : bulkDeleteMutation.mutateAsync({ ids, documentSuperId: documentSuperId as number, skipSectionRewrite });
      inFlight.current = true;
      request
        .then(applyLocally, () => undefined)
        .catch((error) => console.error('Error removing deleted tags from the editor', error))
        .finally(() => {
          inFlight.current = false;
        });
    },
    [inspectEditor, removeFromEditor, documentSuperId, deleteTagMutation, bulkDeleteMutation, setEditorReferences]
  );

  const confirmDelete = useCallback(() => {
    runDelete(state.pending, state.removeCompletely);
    setState(EMPTY_STATE);
  }, [state, runDelete]);

  const confirmDeleteChain = useCallback(() => {
    runDelete(
      state.chain.map((tag) => ({ id: tag.id, tag })),
      false
    );
    setState(EMPTY_STATE);
  }, [state, runDelete]);

  const cancelDelete = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  return {
    showModal: state.showModal,
    pendingCount: state.pending.length,
    chainCount: state.chain.length,
    isDeleting: deleteTagMutation.isPending || bulkDeleteMutation.isPending,
    requestDelete,
    requestDeleteById,
    requestDeleteMany,
    confirmDelete,
    confirmDeleteChain,
    cancelDelete,
  };
}
